import { ChangeEvent, FC, useRef } from 'react';
import { Camera } from 'lucide-react';
import { UserProfile } from '../types';
import { useAvatar } from '../hooks/useAvatar';

interface AvatarUploadProps {
    profile: UserProfile;
    onProfileUpdate: (profile: UserProfile) => void;
    onError: (message: string) => void;
}

export const AvatarUpload: FC<AvatarUploadProps> = ({ profile, onProfileUpdate, onError }) => {
    const { isUploadingAvatar, avatarPreview, handleAvatarChange } = useAvatar({
        onSuccess: onProfileUpdate,
        onError,
    });

    const fileInputRef = useRef<HTMLInputElement>(null);

    const getInitials = (name: string): string => {
        return name
            .split(' ')
            .map((n) => n[0])
            .join('')
            .toUpperCase()
            .slice(0, 2);
    };

    const handleAvatarClick = () => {
        fileInputRef.current?.click();
    };

    const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleAvatarChange(file);
        }
        // Reset inputs
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <div className="flex flex-col items-center text-center">
            <div className="relative mb-4 group">
                {(avatarPreview || profile.avatar) ? (
                    <img
                        src={avatarPreview || profile.avatar}
                        alt={profile.name}
                        className="w-24 h-24 rounded-full object-cover border-2 border-gray-200"
                    />
                ) : (
                    <div className="w-24 h-24 rounded-full bg-gray-900 text-white flex items-center justify-center text-2xl font-semibold border-2 border-gray-200">
                        {getInitials(profile.name)}
                    </div>
                )}

                <button
                    onClick={handleAvatarClick}
                    disabled={isUploadingAvatar}
                    className="absolute inset-0 flex items-center justify-center rounded-full bg-black bg-opacity-50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer disabled:cursor-not-allowed"
                >
                    {isUploadingAvatar ? (
                        <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
                    ) : (
                        <Camera className="w-6 h-6 text-white" />
                    )}
                </button>

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={onFileChange}
                    className="hidden"
                />
            </div>
            <p className="text-xs text-gray-500 mb-4">Click to upload new picture</p>

            <h2 className="text-lg font-semibold text-gray-900">{profile.name}</h2>
            <p className="text-sm text-gray-500 mt-1 capitalize">{profile.role}</p>
        </div>
    );
};
